/**
 * The STRING identity of a `/uploads` facade rejection, read from either field it can arrive on.
 *
 * Two transports reach this code and they populate different fields:
 *  - contact-admin's `api.*Uploads*` -> `_handleResponse` rejects with the parsed JSON body, so the
 *    server's `errorCode` (`UPLOAD_LOST`, `UPLOAD_OPERATION_IN_PROGRESS`, …) lands on
 *    `error.errorCode`, while `error.code` is the NUMERIC status (`t.code ??= r.status`).
 *  - the shared `UploadsFacadeClient` builds its own typed error and puts the same string on
 *    `error.code`.
 *
 * Reading both is what makes one classifier work for both, and it is the difference between the
 * facade's real verdict and the "other"/unknown bucket that used to swallow every 409. `errorCode`
 * wins because it is the field the server actually names; `code` is only a string on the transports
 * that have no `errorCode` at all. A NUMERIC `code` is never an identity — the raw error body sends
 * the HTTP status there.
 *
 * Accepts a raw response body just as happily as a rejection: both are "an object that may carry the
 * facade's error identity", which is why `UploadsFacadeClient` reads its parsed body through here too.
 */
export function readUploadFacadeErrorCode(errorOrBody: unknown): string | undefined {
	try {
		if (!errorOrBody || typeof errorOrBody !== "object") return undefined;
		const candidate = errorOrBody as { code?: unknown; errorCode?: unknown };
		if (typeof candidate.errorCode === "string" && candidate.errorCode.length > 0) return candidate.errorCode;
		return typeof candidate.code === "string" && candidate.code.length > 0 ? candidate.code : undefined;
	} catch {
		// Unreadable third-party error metadata cannot prove any verdict.
		return undefined;
	}
}

/** Matches the exact durable shapes written after a typed facade code, never a code word in prose. */
export function hasPersistedUploadFacadeErrorCode(message: unknown, expectedCode: string): boolean {
	if (typeof message !== "string") return false;
	const normalized = message.trim().toUpperCase();
	const code = expectedCode.toUpperCase();
	return normalized === code || normalized.includes(`[${code}]`);
}

/**
 * The `/uploads` facade's SETTLING conflicts: the server is mid-transition and the same request
 * will succeed once it lands. None of them means the upload failed.
 *
 *  - `UPLOAD_OPERATION_IN_PROGRESS` — a completion/abort reservation is live, or the session is in
 *    the transient `processing` window where `uploadOperationPolicy` conflicts every operation.
 *  - `UPLOAD_MANIFEST_CONFLICT` — a different manifest is reserved for this attempt. The
 *    sweep-completed case (a sealed/ready session whose fingerprint can never match the client's)
 *    no longer reaches here at all: `uploads.complete` now replays the prior completion instead.
 *    Kept as a defensive retry — the remaining producers are races that resolve themselves, and a
 *    retry re-probes rather than terminalizing a file the server may already hold.
 *  - `UPLOAD_RECONCILIATION_REQUIRED` — the server explicitly asks the client to come back and
 *    reconcile. Same settling class; it reached the "other" bucket for the same reason.
 *
 * Before the server started serializing `errorCode`, every one of these classified as "other",
 * which releases the row to `pending` AND throws — so the drain announced a bare "Upload failed"
 * and the next pass reproduced it identically, forever.
 */
export const SETTLING_FACADE_ERROR_CODES: ReadonlySet<string> = new Set([
	"UPLOAD_OPERATION_IN_PROGRESS",
	"UPLOAD_MANIFEST_CONFLICT",
	"UPLOAD_RECONCILIATION_REQUIRED"
]);

/** True when the rejection is one of the settling conflicts above — "not yet", never "never". */
export function isUploadSettlingConflict(error: unknown): boolean {
	const code = readUploadFacadeErrorCode(error);
	return code !== undefined && SETTLING_FACADE_ERROR_CODES.has(code);
}

/**
 * LSP-19422: the code is read straight off the response body, so the SERVER chooses this text, and
 * a declared error carries the resulting message verbatim to the Datadog log line and into the
 * durable `lastError` row. A reflected `<Key>`/`<BucketName>` arriving through the code field is
 * the same hole as one arriving through the message field, and it matches none of the telemetry
 * boundary's value patterns. So the code is admitted only in the shape every real code uses.
 *
 * Uppercase is the existing contract rather than a new restriction: every code the facade emits is
 * SCREAMING_SNAKE (`UPLOAD_LOST`, `UPLOAD_OPERATION_IN_PROGRESS`, `SHARE_EXPIRED`, …), and
 * `SETTLING_FACADE_ERROR_CODES` matches them with a case-sensitive `Set.has`, so a code outside
 * that shape already classifies as nothing. (`hasPersistedUploadFacadeErrorCode` normalises case
 * for its own comparison, so the de-duplication below is unaffected either way.)
 */
const FACADE_ERROR_CODE = /^[A-Z][A-Z0-9_]{0,63}$/;

/**
 * A durable session keeps only the failure MESSAGE (`FileUploadSession.lastError`), never the typed
 * error or its `code`. So any classification later re-derived from the persisted row — the shared
 * `isEvidenceTerminalFailure` marker set — can only see what survives in that string. Stamping the
 * facade's own code into the message is what makes those markers match a real row instead of being
 * silent no-ops.
 *
 * Appends, never replaces: the server's human-readable sentence stays first, and a code mentioned
 * only in prose still receives the structured stamp required after reload.
 */
export function withFacadeErrorCodeInMessage(message: string, error: unknown): string {
	const code = readUploadFacadeErrorCode(error);
	if (!code || !FACADE_ERROR_CODE.test(code)) return message;
	const stamped = hasPersistedUploadFacadeErrorCode(message, code) ? message : `${message} [${code}]`;
	if (code !== "UPLOAD_TERMINAL" || readUploadTerminalDetails(stamped)) return stamped;
	const details = readUploadTerminalDetails(error);
	if (!details) return stamped;
	// Keep model names readable; encode other string targets so delimiters cannot corrupt the marker.
	let targetType = "-";
	try {
		if (details.targetType !== null) {
			targetType = /^[A-Za-z][A-Za-z0-9]*$/.test(details.targetType)
				? details.targetType
				: `~${encodeURIComponent(details.targetType)}`;
		}
	} catch {
		// Malformed Unicode cannot replace the refusal or prove an abandoned target is terminal.
		targetType = "-";
	}
	return `${stamped} [UPLOAD_LIFECYCLE:${details.status}:${targetType}:${details.recoverable === undefined ? "?" : details.recoverable}]`;
}

/**
 * LSP-19605: the `/uploads/:id/progress` refusal — the server's lifecycle gate said "no more
 * progress for this attempt".
 *
 * `UploadsController.progress` answers 409 `UPLOAD_PROGRESS_CONFLICT` once the attempt has left
 * `active` or the session has left the open statuses. Usually that is the driver's final activity
 * heartbeat racing `/complete` (or a sibling tab completing first) — expected sequencing, nothing to
 * retry. It is also the answer for a session that has since failed or been cancelled (an abandoned
 * chunk-stream recording is instead reopened by the heartbeat, as `/urls` does — LSP-22685), so
 * the heartbeat is never treated as evidence that the upload is fine: the driver's own complete /
 * urls / parts calls carry the authoritative verdict and log at their own level. What this code does
 * settle is that the HEARTBEAT itself is not a fault of the client, so both hosts record it as an
 * info breadcrumb (the 2026-09-04 load test produced 131 in seven minutes at error/warn with no
 * signal behind them). Keyed on the facade code, not the status, because every other facade 409 IS
 * actionable.
 */
export const UPLOAD_PROGRESS_CONFLICT_ERROR_CODE = "UPLOAD_PROGRESS_CONFLICT";

/** True when the server's lifecycle gate refused a progress report — see the code above. */
export function isUploadProgressConflict(errorOrBody: unknown): boolean {
	return readUploadFacadeErrorCode(errorOrBody) === UPLOAD_PROGRESS_CONFLICT_ERROR_CODE;
}

/**
 * The server refused a multipart `/complete` because the durable row no longer points at the upload
 * the client is holding — a re-initiate (a sibling tab, a resumed session, a retried initiate) minted
 * a REPLACEMENT upload on the same S3 key, and completing the old part set would assemble it over the
 * object the replacement produced. Added with the contact-api completion identity gate (R-1
 * follow-up: R-1 stopped aborting the superseded upload, so the server now has to refuse it at
 * completion time instead).
 *
 * It is NOT a settling conflict: retrying the identical `/complete` can never succeed, because the
 * uploadId itself is the thing that is stale. And it is NOT bytes-gone evidence either — the local
 * chunks are all still there and still uploadable. The correct outcome is the same recovery lane as a
 * lost target: reinitialize against a fresh upload target from the retained buffers, never terminal,
 * never a discard.
 */
export const UPLOAD_SUPERSEDED_ERROR_CODE = "UPLOAD_SUPERSEDED";

/** True when the server refused this completion because the attempt was replaced — see the code above. */
export function isUploadSupersededError(error: unknown): boolean {
	return readUploadFacadeErrorCode(error) === UPLOAD_SUPERSEDED_ERROR_CODE;
}

/**
 * LSP-22828: a `/uploads` create replay answered `alreadyComplete` — the server already holds this exact
 * upload (same idempotency identity) and minted nothing to transfer, so the reply carries no targets.
 *
 * Client-owned, not a server code: the server answers 200, and it is the shared client's refusal to wrap
 * a targetless reply as a multipart target that raises it. A resumed or reinitialized session that was
 * finished elsewhere (a sibling tab, the server's own sweep) reaches this; the correct outcome is a
 * terminal COMPLETE of the local session, never a retry and never a park. Before this code existed the
 * reply was a plain "requires at least one envelope" Error the drain retried forever.
 */
export const UPLOAD_ALREADY_COMPLETE_AT_CREATE_CODE = "UPLOAD_ALREADY_COMPLETE_AT_CREATE";

export class UploadAlreadyCompleteAtCreateError extends Error {
	readonly code = UPLOAD_ALREADY_COMPLETE_AT_CREATE_CODE;

	constructor(readonly registryId: string) {
		super(`Upload ${registryId} was already complete when it was re-created [${UPLOAD_ALREADY_COMPLETE_AT_CREATE_CODE}]`);
		this.name = "UploadAlreadyCompleteAtCreateError";
	}
}

/** True when a create replay proved the upload already finished — see the code above. */
export function isUploadAlreadyCompleteAtCreate(error: unknown): error is UploadAlreadyCompleteAtCreateError {
	return readUploadFacadeErrorCode(error) === UPLOAD_ALREADY_COMPLETE_AT_CREATE_CODE;
}

/**
 * The legacy multipart `/complete` routes refused this completion because its non-empty part manifest
 * cannot describe the whole object — short of the part count the row recorded at initiate, or not the
 * contiguous 1..N set every staged upload produces. Added with the contact-api manifest completeness
 * gate, the truncation-closing sibling of the identity gate whose refusal is `UPLOAD_SUPERSEDED` above.
 *
 * It is NOT a settling conflict: the server answers the identical `/complete` with the same refusal
 * forever, because the manifest itself is what is wrong — deliberately not `UPLOAD_MANIFEST_CONFLICT`,
 * which the settling set above tells clients to retry verbatim. And it is NOT bytes-gone evidence
 * either: the staged parts are untouched and the local chunks are still present. The correct outcome is
 * the same recovery lane as a lost or superseded target — reinitialize against a fresh upload from the
 * retained buffers, which mints a coherent part ledger and initiate-time expectation — never terminal,
 * never a discard.
 */
export const UPLOAD_MANIFEST_INCOMPLETE_ERROR_CODE = "UPLOAD_MANIFEST_INCOMPLETE";

/** True when the server refused this completion because its part manifest would truncate the object. */
export function isUploadManifestIncompleteError(error: unknown): boolean {
	return readUploadFacadeErrorCode(error) === UPLOAD_MANIFEST_INCOMPLETE_ERROR_CODE;
}

/**
 * The optional machine-readable refusal detail the facade serialises beside `errorCode`
 * (`UploadFacadeError.details`, emitted by fastifyErrorHandler only when present). Read from a
 * raw body or from the typed rejection `UploadsFacadeClient` builds.
 */
export function readUploadFacadeErrorDetails(errorOrBody: unknown): Record<string, unknown> | undefined {
	try {
		if (!errorOrBody || typeof errorOrBody !== "object") return undefined;
		const details = (errorOrBody as { details?: unknown }).details;
		return details && typeof details === "object" && !Array.isArray(details) ? (details as Record<string, unknown>) : undefined;
	} catch {
		// Unreadable third-party error metadata cannot prove any verdict.
		return undefined;
	}
}

/**
 * Chunked-object completion refused because the declared parts have not all landed as declared
 * (`verifyListedParts`: missing, size mismatch, duplicate, not a single PUT). NOT settling — the
 * identical `/complete` fails until the named ordinals are re-uploaded — and NOT lost: the attempt
 * is intact and every other part is verified. The client re-marks exactly those ordinals as not
 * uploaded and lets the ordinary chunk loop send them again.
 */
export const UPLOAD_PARTS_INCOMPLETE_ERROR_CODE = "UPLOAD_PARTS_INCOMPLETE";

/** True when a chunked-object completion was refused because parts are still owed — see the code above. */
export function isUploadPartsIncompleteError(error: unknown): boolean {
	return readUploadFacadeErrorCode(error) === UPLOAD_PARTS_INCOMPLETE_ERROR_CODE;
}

/**
 * The ordinals a `UPLOAD_PARTS_INCOMPLETE` refusal says are still owed: `details.missingOrdinals`,
 * or the one `details.ordinal` whose size disagreed.
 */
export function readUploadPartsIncompleteOrdinals(errorOrBody: unknown): number[] | undefined {
	if (!isUploadPartsIncompleteError(errorOrBody)) return undefined;
	const details = readUploadFacadeErrorDetails(errorOrBody);
	if (!details) return undefined;
	const isOrdinal = (value: unknown): value is number => Number.isInteger(value) && (value as number) >= 1;
	// Same defensive contract as `readUploadFacadeErrorCode` / `readUploadFacadeErrorDetails` above:
	// this reads THIRD-PARTY error metadata, and the callers that consult it (the driver's
	// parts-incomplete arms, one of which is not inside a try) treat "unreadable" as "nothing
	// actionable" — never as a thrown failure that escapes the recovery and fails the upload.
	try {
		// A PRESENT `missingOrdinals` array wins even when it filters down to empty: the server named the
		// field, so `details.ordinal` is not the answer, and `[]` truthfully reports "nothing actionable".
		if (Array.isArray(details.missingOrdinals)) return details.missingOrdinals.filter(isOrdinal);
		if (isOrdinal(details.ordinal)) return [details.ordinal];
		return undefined;
	} catch {
		return undefined;
	}
}

/** The server rejected the manifest at create (400): the client falls back to a plain multipart create once. */
export const UPLOAD_MANIFEST_INVALID_ERROR_CODE = "UPLOAD_MANIFEST_INVALID";

/** True when the server refused the chunk manifest offered at create — see the code above. */
export function isUploadManifestInvalidError(error: unknown): boolean {
	return readUploadFacadeErrorCode(error) === UPLOAD_MANIFEST_INVALID_ERROR_CODE;
}

/**
 * Assembly produced an object whose ETag/size did not verify (500). The parts are KEPT server-side;
 * the same `/complete` may succeed on retry.
 */
export const CHUNKED_OBJECT_ASSEMBLY_INTEGRITY_ERROR_CODE = "CHUNKED_OBJECT_ASSEMBLY_INTEGRITY";

/** True when chunked-object assembly failed its own integrity check — see the code above. */
export function isChunkedObjectAssemblyIntegrityError(error: unknown): boolean {
	return readUploadFacadeErrorCode(error) === CHUNKED_OBJECT_ASSEMBLY_INTEGRITY_ERROR_CODE;
}

/**
 * Lifecycle facts survive string-only IndexedDB storage; no server prose is accepted as evidence.
 * Markers are [UPLOAD_LIFECYCLE:status:target:recoverable] with true/false/? as the final token.
 * Legacy markers without that token remain readable, but failed without explicit false is retryable.
 */
export function readUploadTerminalDetails(error: unknown): { status: "failed" | "cancelled" | "abandoned"; targetType: string | null; discardReason: string | null; recoverable?: boolean } | undefined {
	try {
		if (readUploadFacadeErrorCode(error) === "UPLOAD_TERMINAL") {
			const details = readUploadFacadeErrorDetails(error);
			if (details && (details.status === "failed" || details.status === "cancelled" || details.status === "abandoned")) {
				const targetType = typeof details.targetType === "string" ? details.targetType : null;
				return { status: details.status, targetType, discardReason: typeof details.discardReason === "string" ? details.discardReason : null, recoverable: typeof details.recoverable === "boolean" ? details.recoverable : undefined };
			}
			// A typed refusal with missing/unknown details cannot borrow a verdict from its prose.
			return undefined;
		}
		const message = typeof error === "string" ? error : error instanceof Error ? error.message : undefined;
		if (message && hasPersistedUploadFacadeErrorCode(message, "UPLOAD_TERMINAL")) {
			const match = /\[UPLOAD_LIFECYCLE:(failed|cancelled|abandoned):(-|[A-Za-z][A-Za-z0-9]*|~[^\]:\s]*)(?::(true|false|\?))?\]/.exec(message);
			if (match) {
				const targetType = match[2] === "-" ? null : match[2].startsWith("~") ? decodeURIComponent(match[2].slice(1)) : match[2];
				return { status: match[1] as "failed" | "cancelled" | "abandoned", targetType, discardReason: null, recoverable: match[3] === "false" ? false : match[3] === "true" ? true : undefined };
			}
		}
		return undefined;
	} catch {
		return undefined;
	}
}

export function isUploadTerminalFailure(error: unknown): boolean {
	const details = readUploadTerminalDetails(error);
	return details !== undefined && ((details.status === "failed" && details.recoverable === false) || details.status === "cancelled" || (details.status === "abandoned" && details.targetType !== null && details.targetType !== "RecordingSession"));
}

/** The typed refusal stays distinct from generic 404 auth arithmetic even when its details are unknown. */
export function isUploadTerminalRefusal(error: unknown): boolean {
	if (readUploadFacadeErrorCode(error) === "UPLOAD_TERMINAL") return true;
	const message = typeof error === "string" ? error : error instanceof Error ? error.message : undefined;
	return hasPersistedUploadFacadeErrorCode(message, "UPLOAD_TERMINAL");
}

export type UploadRefusalTrigger = "urls" | "parts" | "objectStatus" | "complete";
const uploadRefusalTriggers = new WeakMap<object, UploadRefusalTrigger>();

/** Correlate a refusal with its request without mutating frozen/reused server error bodies. */
export function tagUploadRefusalTrigger(error: unknown, trigger: UploadRefusalTrigger): unknown {
	if (error && typeof error === "object") uploadRefusalTriggers.set(error, trigger);
	return error;
}

/** Normalizers preserve the original rejection as cause; URL refresh wraps it as originalError. */
export function readUploadRefusalTrigger(error: unknown): UploadRefusalTrigger | undefined {
	try {
		for (let depth = 0; error && typeof error === "object" && depth < 8; depth++) {
			const trigger = uploadRefusalTriggers.get(error);
			if (trigger) return trigger;
			const wrapped = error as { cause?: unknown; originalError?: unknown };
			error = wrapped.cause ?? wrapped.originalError;
		}
	} catch { /* Unreadable wrappers cannot establish the originating request. */ }
	return undefined;
}
